import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { selectPaneDocuments } from "../lib/pane-selection";
import { KeyingPane } from "./KeyingPane";
import { enterTransaction, leaveTransaction } from "../api/client";
import { useStationStore } from "../state/station-store";
import type { StationTransaction } from "../api/client";
import styles from "./RemittanceStation.module.css";

// F-013 — the panes render the shared FieldPanel/FieldInput, whose blur-time
// client validation reads ContentValidateContext. The provider lives at the
// station root (StationContentValidateProvider, hoisted Wave F 19e) so the
// bundle fetches once per batch instead of once per transaction switch.
export function TransactionPanes({ transaction }: { transaction: StationTransaction }) {
  const setDocAmount = useStationStore((s) => s.setDocAmount);
  const sel = useMemo(() => selectPaneDocuments(transaction), [transaction]);
  const [focusedPane, setFocusedPane] = useState<"primary" | "companion">("primary");
  // Lazy-init: `useRef(crypto.randomUUID())` would mint (and discard) a fresh
  // UUID on every render since the arg isn't lazy — only the first one sticks.
  const connectionIdRef = useRef<string | undefined>(undefined);
  if (!connectionIdRef.current) connectionIdRef.current = crypto.randomUUID();
  const connectionId = connectionIdRef.current;

  // Per-doc lock lifecycle: acquire on enter, release on leave. The txn id in
  // deps means switching transactions releases the old locks and takes the new.
  useEffect(() => {
    let active = true;
    void enterTransaction(transaction.id, connectionId).catch(() => {});
    return () => {
      if (active) void leaveTransaction(transaction.id).catch(() => {});
      active = false;
    };
  }, [transaction.id]);

  // F6 / Ctrl+Tab switches pane focus (design §6).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "F6" || (e.ctrlKey && e.key === "Tab")) {
        e.preventDefault();
        setFocusedPane((p) => (p === "primary" ? "companion" : "primary"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onAmountCents = useCallback((docId: string, cents: number | null) => setDocAmount(docId, cents), [setDocAmount]);

  // Finishing the focused pane's last field advances focus to the companion,
  // then (companion done) leaves focus on the balance panel (design §6). The
  // right panel owns "all keyed → focus Accept"; here we just hop panes.
  const onPrimaryExhausted = useCallback(() => { if (sel.companion) setFocusedPane("companion"); }, [sel.companion]);
  const onCompanionExhausted = useCallback(() => { /* both keyed — Task 14's panel focuses Accept */ }, []);

  if (!sel.primary) return <div className={styles.center} data-testid="station-center">No keyable documents in this transaction.</div>;

  return (
    <main className={styles.center} data-testid="station-center">
      <div className={styles.paneRow}>
        <KeyingPane
          doc={sel.primary}
          amountField={sel.primary.amountFieldName}
          isFocused={focusedPane === "primary"}
          onFocus={() => setFocusedPane("primary")}
          onAmountCents={onAmountCents}
          onExhausted={onPrimaryExhausted}
        />
        {sel.companion && (
          <KeyingPane
            doc={sel.companion}
            amountField={sel.companion.amountFieldName}
            isFocused={focusedPane === "companion"}
            onFocus={() => setFocusedPane("companion")}
            onAmountCents={onAmountCents}
            onExhausted={onCompanionExhausted}
          />
        )}
      </div>
      {sel.thumbnails.length > 0 && (
        <div className={styles.thumbStrip} aria-label="Other documents">
          {sel.thumbnails.map((d) => <span key={d.id} className={styles.thumb}>{d.displayId ?? d.filename}</span>)}
        </div>
      )}
    </main>
  );
}
